import React, { useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowRight, Check, Sparkles } from "lucide-react";
import ReCAPTCHA from "react-google-recaptcha";
import toast from "react-hot-toast";

// Show the popup after the visitor has spent this long on the page.
const TRIGGER_DELAY_MS = 30000; // 30s
// Only show once per browser session.
const SESSION_KEY = "tg_engagement_popup_shown";

/**
 * Engagement popup with a mini contact form.
 * Mounted only on the landing page. Triggers after 30s, once per session,
 * and submits to the same /api/v1/contact endpoint the Contact page uses.
 */
const EngagementPopup = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const [form, setForm] = useState({
    fullName: "",
    email: "",
    company: "",
    phone: "",
    message: "",
  });
  const recaptchaRef = useRef(null);

  // 30s timer; skip entirely if already shown this session.
  useEffect(() => {
    let alreadyShown = false;
    try {
      alreadyShown = sessionStorage.getItem(SESSION_KEY) === "1";
    } catch (e) {
      // sessionStorage may be unavailable (private mode); fail open and allow showing.
    }
    if (alreadyShown) return;

    const timer = setTimeout(() => {
      setIsOpen(true);
      try {
        sessionStorage.setItem(SESSION_KEY, "1");
      } catch (e) {
        /* ignore */
      }
    }, TRIGGER_DELAY_MS);

    return () => clearTimeout(timer);
  }, []);

  // Close on Escape.
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") setIsOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen]);

  const close = () => setIsOpen(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Phone: 7–15 digits, optional +, spaces/dashes/dots/parens allowed
    const phoneDigits = form.phone.replace(/\D/g, "");
    if (phoneDigits.length < 7 || phoneDigits.length > 15) {
      toast.error("Please enter a valid phone number.");
      return;
    }

    if (!recaptchaToken) {
      toast.error("Please complete the reCAPTCHA verification.");
      return;
    }

    setIsSubmitting(true);

    // GTM tracking (same shape as the Contact page form).
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "form_submit",
      form_id: "engagement-popup-form",
      formText: "Send",
      formUrl: window.location.href,
    });

    try {
      const userTimezone =
        typeof Intl !== "undefined" &&
        Intl.DateTimeFormat?.().resolvedOptions?.().timeZone
          ? Intl.DateTimeFormat().resolvedOptions().timeZone
          : "UTC";

      // Message must be >= 10 chars (backend rule); fall back to a safe default
      // when the optional field is empty or too short.
      const trimmedMsg = (form.message || "").trim();
      const message =
        trimmedMsg.length >= 10
          ? trimmedMsg
          : "Reached out via the landing-page popup.";

      const payload = {
        type: "contact_us",
        inquiryType: "General Inquiry",
        fullName: form.fullName,
        email: form.email,
        companyName: form.company,
        phone: form.phone,
        subject: "Lead from landing popup",
        message,
        userTimezone,
        recaptchaToken,
      };

      const response = await fetch(
        `${
          process.env.NODE_ENV === "development"
            ? "http://52.66.103.58"
            : window.location.origin
        }/api/v1/contact`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        let errorMessage =
          errorData.message || `HTTP error! status: ${response.status}`;
        if (errorData.details && typeof errorData.details === "object") {
          const fieldErrors = Object.values(errorData.details);
          if (fieldErrors.length > 0) {
            errorMessage = `${errorMessage} ${fieldErrors.join(". ")}`;
          }
        }
        throw new Error(errorMessage);
      }

      await response.json();
      setSubmitted(true); // show the thank-you state inside the popup
    } catch (error) {
      console.error("Error submitting popup form:", error);
      toast.error(error.message || "Failed to send message. Please try again.");
    } finally {
      setIsSubmitting(false);
      setRecaptchaToken(null);
      if (recaptchaRef.current) recaptchaRef.current.reset();
    }
  };

  // Render via a portal into <body> so no transformed/overflow-hidden ancestor
  // on the landing page can break the `position: fixed` centering.
  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-[80] flex items-center justify-center overflow-y-auto bg-black/50 p-4 backdrop-blur-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={close}
        >
          {/* Modal — stopPropagation so clicks inside don't close it */}
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-label="Get in touch with TeamGrid"
            onClick={(e) => e.stopPropagation()}
            className="relative z-[90] flex max-h-[92vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl md:flex-row"
            initial={{ opacity: 0, scale: 0.92, y: 12 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.92, y: 12 }}
            transition={{ duration: 0.25 }}
          >
            {/* Close button (floats over both panels) */}
            <button
              type="button"
              onClick={close}
              aria-label="Close"
              className="absolute right-3 top-3 z-10 rounded-full p-1 text-gray-400 transition hover:bg-gray-100 hover:text-gray-700 md:text-white/70 md:hover:bg-white/20 md:hover:text-white"
            >
              <X className="h-5 w-5" />
            </button>

            {/* LEFT — visual / message panel */}
            <div className="relative flex flex-col justify-center overflow-hidden bg-gradient-to-br from-[#0F766E] via-[#0D9488] to-[#00D1C1] p-6 text-white md:w-[44%] md:p-8">
              {/* decorative blobs */}
              <div className="pointer-events-none absolute -right-10 -top-10 h-32 w-32 rounded-full bg-white/10 blur-2xl" />
              <div className="pointer-events-none absolute -bottom-12 -left-8 h-36 w-36 rounded-full bg-black/10 blur-2xl" />

              <div className="relative">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1 text-xs font-semibold backdrop-blur-sm">
                  <Sparkles className="h-3.5 w-3.5" /> AI workforce intelligence
                </span>
                <h3 className="mt-4 text-2xl text-white font-bold leading-snug md:text-[28px]">
                  See TeamGrid in action
                </h3>
                <p className="mt-2 text-sm text-white/85">
                  Understand how your team really works — without
                  micromanaging.
                </p>

                <ul className="mt-5 space-y-3 text-sm">
                  {[
                    "Automatic time & activity tracking",
                    "AI summaries of real work done",
                    "Spot burnout & blockers early",
                  ].map((point) => (
                    <li key={point} className="flex items-start gap-2.5">
                      <span className="mt-0.5 flex h-5 w-5 flex-none items-center justify-center rounded-full bg-white/20">
                        <Check className="h-3.5 w-3.5" />
                      </span>
                      <span className=" !text-white/95">{point}</span>
                    </li>
                  ))}
                </ul>

                <p className="mt-6 text-xs text-white/70">
                  Trusted by growing teams to ship more, with clarity.
                </p>
              </div>
            </div>

            {/* RIGHT — form panel (scrolls if tall on small screens) */}
            <div className="flex-1 overflow-y-auto p-6 sm:p-8">
              {submitted ? (
                <div className="flex h-full flex-col items-center justify-center py-6 text-center">
                  <h3 className="text-2xl font-bold text-gray-900">
                    Thanks for reaching out! 🎉
                  </h3>
                  <p className="mt-2 text-gray-600">
                    Our team will get back to you shortly.
                  </p>
                  <button
                    type="button"
                    onClick={close}
                    className="mt-6 rounded-xl bg-[#00D1C1] px-6 py-3 font-bold text-black transition hover:opacity-90"
                  >
                    Close
                  </button>
                </div>
              ) : (
                <>
                  <h4 className="text-lg font-bold text-gray-900">
                    Book a quick walkthrough
                  </h4>
                  <p className="mt-1 text-sm text-gray-500">
                    Drop your details — we'll reach out shortly.
                  </p>

                  <form
                    id="engagement-popup-form"
                    onSubmit={handleSubmit}
                    className="mt-4 space-y-3"
                  >
                    <div>
                      <label
                        htmlFor="popup-name"
                        className="mb-1 block text-xs font-medium text-gray-600"
                      >
                        Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        id="popup-name"
                        type="text"
                        name="fullName"
                        required
                        value={form.fullName}
                        onChange={handleChange}
                        placeholder="Your name"
                        className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-gray-900 placeholder-gray-400 focus:border-[#00D1C1] focus:ring-2 focus:ring-[#00D1C1]/40"
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="popup-email"
                        className="mb-1 block text-xs font-medium text-gray-600"
                      >
                        Email <span className="text-red-500">*</span>
                      </label>
                      <input
                        id="popup-email"
                        type="email"
                        name="email"
                        required
                        value={form.email}
                        onChange={handleChange}
                        placeholder="you@company.com"
                        className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-gray-900 placeholder-gray-400 focus:border-[#00D1C1] focus:ring-2 focus:ring-[#00D1C1]/40"
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="popup-company"
                        className="mb-1 block text-xs font-medium text-gray-600"
                      >
                        Company <span className="text-red-500">*</span>
                      </label>
                      <input
                        id="popup-company"
                        type="text"
                        name="company"
                        required
                        minLength={2}
                        value={form.company}
                        onChange={handleChange}
                        placeholder="Your company"
                        className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-gray-900 placeholder-gray-400 focus:border-[#00D1C1] focus:ring-2 focus:ring-[#00D1C1]/40"
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="popup-phone"
                        className="mb-1 block text-xs font-medium text-gray-600"
                      >
                        Phone <span className="text-red-500">*</span>
                      </label>
                      <input
                        id="popup-phone"
                        type="tel"
                        name="phone"
                        required
                        value={form.phone}
                        onChange={(e) => {
                          // Strip anything that's not a digit, +, space, -, ., (, )
                          const cleaned = e.target.value.replace(/[^\d\+\s\-\.\(\)]/g, "");
                          setForm((prev) => ({ ...prev, phone: cleaned }));
                        }}
                        placeholder="+1 555 000 0000"
                        pattern="[\+\d\s\-\.\(\)]{7,20}"
                        title="Enter a valid phone number (7–15 digits)"
                        className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-gray-900 placeholder-gray-400 focus:border-[#00D1C1] focus:ring-2 focus:ring-[#00D1C1]/40"
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="popup-message"
                        className="mb-1 block text-xs font-medium text-gray-600"
                      >
                        Message{" "}
                        <span className="font-normal text-gray-400">(optional)</span>
                      </label>
                      <textarea
                        id="popup-message"
                        name="message"
                        rows={3}
                        value={form.message}
                        onChange={handleChange}
                        placeholder="What would you like to know?"
                        className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-gray-900 placeholder-gray-400 focus:border-[#00D1C1] focus:ring-2 focus:ring-[#00D1C1]/40"
                      />
                    </div>

                    {/* reCAPTCHA scales down a touch so it never overflows the narrow column */}
                    <div className="flex origin-left scale-90 justify-start sm:scale-100">
                      <ReCAPTCHA
                        ref={recaptchaRef}
                        sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                        onChange={(token) => setRecaptchaToken(token)}
                        onExpired={() => setRecaptchaToken(null)}
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={isSubmitting || !recaptchaToken}
                      className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#00D1C1] py-3.5 font-bold text-black transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {isSubmitting ? "Sending..." : "Send"}
                      {!isSubmitting && <ArrowRight className="h-5 w-5" />}
                    </button>

                    <p className="text-center text-xs text-gray-400">
                      By submitting, you agree to our{" "}
                      <a
                        href="/privacy-policy"
                        className="underline hover:opacity-80"
                      >
                        Privacy Policy
                      </a>
                      .
                    </p>
                  </form>
                </>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default EngagementPopup;
